import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Container, Trip } from '../types/container'
import * as containerService from '../services/containerService'

interface ContainerContextType {
  containers: Container[]
  trips: Trip[]
  selectedContainer: Container | null
  setSelectedContainer: (container: Container | null) => void
  getExpectedContainers: () => Container[]
  getOnYardContainers: () => Container[]
  refreshData: () => void
  loading: boolean
}

const ContainerContext = createContext<ContainerContextType | undefined>(undefined)

export const ContainerProvider = ({ children }: { children: ReactNode }) => {
  const [containers, setContainers] = useState<Container[]>([])
  const [trips, setTrips] = useState<Trip[]>([])
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null)
  const [loading, setLoading] = useState(true)

  // Initialize data on mount
  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [loadedContainers, loadedTrips] = await Promise.all([
        containerService.getContainers(),
        containerService.getTrips()
      ])
      setContainers(loadedContainers)
      setTrips(loadedTrips)
    } catch (error) {
      console.error('Failed to load data:', error)
    } finally {
      setLoading(false)
    }
  }

  const refreshData = () => {
    loadData()
  }

  const getExpectedContainers = () => {
    return containers.filter(c => c.status === 'expected')
  }

  const getOnYardContainers = () => {
    return containers.filter(c => c.status === 'on_yard')
  }

  return (
    <ContainerContext.Provider
      value={{
        containers,
        trips,
        selectedContainer,
        setSelectedContainer,
        getExpectedContainers,
        getOnYardContainers,
        refreshData,
        loading
      }}
    >
      {children}
    </ContainerContext.Provider>
  )
}

export const useContainer = () => {
  const context = useContext(ContainerContext)
  if (!context) {
    throw new Error('useContainer must be used within ContainerProvider')
  }
  return context
}
